import React, { useEffect, useState } from "react";
import api, { resolveImageUrl } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { formatPHP } from "@/lib/format";

const EMPTY = () => ({
  slug: "", name: "", short_description: "", description: "",
  specifications: [], product_type: "hardware", category: "",
  sku: "", price: 0, compare_at_price: 0, stock_quantity: 0,
  featured_image: "", gallery: [], is_featured: false, is_active: true, related_products: [],
});

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/admin/products"), api.get("/admin/categories")]);
    setItems(p.data || []); setCategories(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, editing);
      else await api.post(`/admin/products`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">Shop</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Manage Products</h1>
        </div>
        <button onClick={() => setEditing(EMPTY())} className="btn-msr-primary" data-testid="admin-products-add-btn">
          <Plus size={14} /> Add Product
        </button>
      </div>

      <div className="bg-white border border-msr-line">
        <table className="w-full text-sm">
          <thead className="bg-msr-mist text-left text-[11px] uppercase tracking-wider text-msr-slate">
            <tr>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-msr-line" data-testid={`admin-product-row-${p.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-msr-mist overflow-hidden">
                      {p.featured_image && <img src={resolveImageUrl(p.featured_image)} className="h-full w-full object-cover" alt="" />}
                    </div>
                    <div>
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-xs text-msr-slate">{p.category}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-msr-slate uppercase text-xs">{p.product_type}</td>
                <td className="px-4 py-3 text-msr-slate">{p.sku}</td>
                <td className="px-4 py-3 font-semibold">{formatPHP(p.price)}</td>
                <td className="px-4 py-3">{p.stock_quantity}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...EMPTY(), ...p })} className="text-msr-blue inline-flex items-center gap-1 text-xs mr-3" data-testid={`admin-product-edit-${p.slug}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => remove(p.id)} className="text-red-500 inline-flex items-center gap-1 text-xs" data-testid={`admin-product-delete-${p.slug}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductEditor
          value={editing}
          onChange={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
          categories={categories}
          allProducts={items}
        />
      )}
    </div>
  );
}

function ProductEditor({ value, onChange, onSave, onClose, categories, allProducts }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="fixed inset-0 z-40 bg-black/60 grid place-items-center p-4" data-testid="admin-product-editor">
      <div className="bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto border-t-4 border-msr-blue">
        <div className="sticky top-0 bg-white border-b border-msr-line px-6 py-4 flex justify-between z-10">
          <div>
            <div className="overline">Editing product</div>
            <div className="font-heading font-bold text-lg">{value.name || "New product"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={onSave} className="btn-msr-primary py-2 px-4 text-xs" data-testid="admin-product-save-btn">Save</button>
            <button onClick={onClose} className="p-2 text-msr-slate" data-testid="admin-product-close-btn"><X size={20} /></button>
          </div>
        </div>
        <div className="p-6 grid lg:grid-cols-2 gap-5">
          <Field label="Slug *" value={value.slug} onChange={(v) => set("slug", v)} testId="prod-edit-slug" />
          <Field label="Name *" value={value.name} onChange={(v) => set("name", v)} testId="prod-edit-name" />
          <Select label="Type" value={value.product_type} onChange={(v) => set("product_type", v)} options={["software", "hardware", "service", "bundle"]} testId="prod-edit-type" />
          <Select label="Category" value={value.category} onChange={(v) => set("category", v)} options={["", ...categories.map((c) => c.slug)]} testId="prod-edit-category" />
          <Field label="SKU" value={value.sku} onChange={(v) => set("sku", v)} testId="prod-edit-sku" />
          <Field label="Price (PHP)" type="number" value={value.price} onChange={(v) => set("price", Number(v))} testId="prod-edit-price" />
          <Field label="Compare-at price (PHP)" type="number" value={value.compare_at_price} onChange={(v) => set("compare_at_price", Number(v))} testId="prod-edit-compare" />
          <Field label="Stock quantity" type="number" value={value.stock_quantity} onChange={(v) => set("stock_quantity", Number(v))} testId="prod-edit-stock" />
          <div className="lg:col-span-2"><TextArea label="Short description" value={value.short_description} onChange={(v) => set("short_description", v)} testId="prod-edit-short" /></div>
          <div className="lg:col-span-2"><TextArea label="Description" value={value.description} onChange={(v) => set("description", v)} rows={4} testId="prod-edit-desc" /></div>
          <ImageUploader label="Featured image" value={value.featured_image} onChange={(v) => set("featured_image", v)} testId="prod-edit-featured" />
          <ToggleField label="Featured" value={value.is_featured} onChange={(v) => set("is_featured", v)} testId="prod-edit-is-featured" />
          <div className="lg:col-span-2">
            <GalleryEditor items={value.gallery || []} onChange={(items) => set("gallery", items)} />
          </div>
          <div className="lg:col-span-2">
            <SpecsEditor items={value.specifications || []} onChange={(items) => set("specifications", items)} />
          </div>
          <div className="lg:col-span-2">
            <RelatedEditor items={value.related_products || []} onChange={(items) => set("related_products", items)} all={allProducts.filter((p) => p.id !== value.id)} />
          </div>
          <ToggleField label="Active" value={value.is_active} onChange={(v) => set("is_active", v)} testId="prod-edit-active" />
        </div>
      </div>
    </div>
  );
}

function GalleryEditor({ items, onChange }) {
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Gallery</div>
        <button onClick={add} className="text-xs bg-msr-blue text-white px-3 py-1.5 inline-flex items-center gap-1" data-testid="prod-gallery-add"><Plus size={12} /> Add</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((img, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`prod-gallery-${i}`}>
            <div className="flex-1"><ImageUploader value={img} onChange={(v) => update(i, v)} label={`Image ${i + 1}`} testId={`prod-gallery-uploader-${i}`} /></div>
            <button onClick={() => remove(i)} className="text-red-500 mt-7"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpecsEditor({ items, onChange }) {
  const add = () => onChange([...items, { key: "", value: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Specifications</div>
        <button onClick={add} className="text-xs bg-msr-blue text-white px-3 py-1.5 inline-flex items-center gap-1" data-testid="prod-spec-add"><Plus size={12} /> Add spec</button>
      </div>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`prod-spec-${i}`}>
            <input value={s.key} onChange={(e) => update(i, "key", e.target.value)} placeholder="Key" className="col-span-4 border border-msr-line px-2 py-2 text-sm" />
            <input value={s.value} onChange={(e) => update(i, "value", e.target.value)} placeholder="Value" className="col-span-7 border border-msr-line px-2 py-2 text-sm" />
            <button onClick={() => remove(i)} className="text-red-500 col-span-1 grid place-items-center"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RelatedEditor({ items, onChange, all }) {
  const toggle = (id) => {
    if (items.includes(id)) onChange(items.filter((i) => i !== id));
    else onChange([...items, id]);
  };
  return (
    <div>
      <div className="overline mb-3">Related products</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto border border-msr-line p-3 bg-msr-mist/40">
        {all.map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-sm bg-white border border-msr-line px-3 py-2 cursor-pointer" data-testid={`prod-related-${p.slug}`}>
            <input type="checkbox" checked={items.includes(p.id)} onChange={() => toggle(p.id)} className="accent-msr-blue" />
            <span className="truncate">{p.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue" data-testid={testId} />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue resize-none" data-testid={testId} />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink focus:outline-none focus:border-msr-blue" data-testid={testId}>
        {options.map((o) => (<option key={o} value={o}>{o || "Select..."}</option>))}
      </select>
    </label>
  );
}

function ToggleField({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 py-2.5" data-testid={testId}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-msr-blue" />
      <span className="text-sm font-semibold text-msr-ink">{label}</span>
    </label>
  );
}
